import { useMemo } from 'react'

import styled from 'styled-components'

import { DisplayName } from './DisplayBlock.style'
import { SectionCard } from './Section'
import {
  getTemplateImages,
  getTemplatePdfAttachments,
} from './utils/getTemplateImages'

import DocumentViewer from '@/components/shared-components/DocumentViewer'
import PreventPageBreak from '@/components/shared-components/PrintMedia'
import { useTemplateRenderer } from '@/context/templateRendererContext'

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 2rem;
`

const ImageToMarkup = styled.img`
  margin-top: 1px; // random 1px line was appearing on page breaks. So dumb
  max-width: 95vw;
  max-height: 85vh; // leave some room for the PDF footer so it's not on its own page
  object-fit: contain;
  object-position: center;
`

export const TemplateAppendix: React.FC = () => {
  const { renderOptions, templateResponseData } = useTemplateRenderer()

  const images = useMemo(
    () =>
      renderOptions.renderingInPdf
        ? getTemplateImages(templateResponseData)
        : [],
    [renderOptions.renderingInPdf, templateResponseData]
  )

  const pdfs = useMemo(
    () =>
      renderOptions.renderingInPdf
        ? getTemplatePdfAttachments(templateResponseData)
        : [],
    [renderOptions.renderingInPdf, templateResponseData]
  )

  const showPdfAppendix = renderOptions.pdfAppendix && pdfs.length

  if (!images.length && !showPdfAppendix) return null

  return (
    <SectionCard
      section={{
        name: 'Appendix',
        description:
          'Find all full-size attachments used in this template below',
        blockGroups: [],
      }}
      index={templateResponseData.sections.length + 1}
    >
      {images?.length && (
        <Wrapper>
          {images
            .filter((i) => i.url)
            .map((image) => (
              <PreventPageBreak key={image.id} shouldAvoidPageBreak>
                <ImageToMarkup
                  src={image.url}
                  alt="Appendix image"
                  onError={(e) => {
                    e.currentTarget.style.display = 'none' // hide failed images
                  }}
                />
              </PreventPageBreak>
            ))}
        </Wrapper>
      )}
      {showPdfAppendix && typeof window !== 'undefined' && (
        <Wrapper>
          {/* This doesn't work when rendering serverside FYI - pdf lib issue - but a download will stitch the pdfs together */}
          {pdfs.map((pdf) => (
            <PreventPageBreak key={pdf.id} shouldAvoidPageBreak>
              {pdf.name && <DisplayName>{pdf.name}</DisplayName>}
              <DocumentViewer
                url={pdf.url}
                name={pdf.name}
                showAllPages={true}
              />
            </PreventPageBreak>
          ))}
        </Wrapper>
      )}
    </SectionCard>
  )
}
